"use client";

import * as React from "react";
import type { Column, Table } from "@tanstack/react-table";
import {
  ArrowDown,
  ArrowUp,
  EyeOff,
  MoreVertical,
  Pin,
  PinOff,
} from "lucide-react";
import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@aio/platform-ui";

/**
 * Props for {@link ColumnMenu}.
 *
 * The menu is a thin presentational layer over TanStack Table's per-column
 * helpers — it never owns sort/visibility/pinning state itself. Disabled items
 * fall back to a `data-disabled` attribute (driven by the column's own
 * `getCanSort` / `getCanHide` / `getCanPin` capability flags) so the consumer
 * never has to filter the menu set themselves.
 */
export interface ColumnMenuProps<TData> {
  /** The column this menu acts on. */
  column: Column<TData, unknown>;
  /** The owning table (used for column-pinning state checks). */
  table: Table<TData>;
  /** Optional override for the menu's accessible label. */
  triggerAriaLabel?: string;
  /** Extra classes for the trigger button. */
  className?: string;
}

/**
 * Per-column dropdown menu providing sort, pin, and hide controls.
 *
 * Items:
 * - Sort ascending — toggles to ascending direction.
 * - Sort descending — toggles to descending direction.
 * - Clear sort — calls `column.clearSorting()`.
 * - Pin left / Pin right — calls `column.pin(side)`.
 * - Unpin — only shown when the column is currently pinned.
 * - Hide column — calls `column.toggleVisibility(false)`.
 *
 * Items are conditionally rendered based on capability:
 * - Sort items disappear when `column.getCanSort()` is false.
 * - Pin items disappear when `column.getCanPin()` is false (or the table has
 *   `enableColumnPinning: false`).
 * - Hide is disabled (greyed) when `column.getCanHide()` is false.
 */
export function ColumnMenu<TData>({
  column,
  table,
  triggerAriaLabel,
  className,
}: ColumnMenuProps<TData>): React.JSX.Element {
  const canSort = column.getCanSort();
  const canPin =
    column.getCanPin() &&
    // Respect the table-level pinning toggle, mirroring the rest of TanStack's
    // capability surface (table options override column options).
    table.options.enableColumnPinning !== false;
  const canHide = column.getCanHide();
  const pinned = column.getIsPinned();
  const headerLabel =
    typeof column.columnDef.header === "string"
      ? column.columnDef.header
      : column.id;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={triggerAriaLabel ?? `Column options for ${headerLabel}`}
          className={cn("size-7", className)}
          // Stop propagation so the trigger click doesn't also toggle sorting
          // when the menu lives inside a sortable header button.
          onClick={(e) => e.stopPropagation()}
          data-testid={`column-menu-trigger-${column.id}`}
        >
          <MoreVertical className="size-4" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-44">
        <DropdownMenuLabel>{headerLabel}</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {canSort ? (
          <>
            <DropdownMenuItem
              onSelect={() => column.toggleSorting(false)}
              data-testid={`column-menu-sort-asc-${column.id}`}
            >
              <ArrowUp className="size-4" aria-hidden="true" />
              <span>Sort ascending</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => column.toggleSorting(true)}
              data-testid={`column-menu-sort-desc-${column.id}`}
            >
              <ArrowDown className="size-4" aria-hidden="true" />
              <span>Sort descending</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => column.clearSorting()}
              disabled={column.getIsSorted() === false}
              data-testid={`column-menu-sort-clear-${column.id}`}
            >
              <span className="inline-block size-4" aria-hidden="true" />
              <span>Clear sort</span>
            </DropdownMenuItem>
            {canPin || canHide ? <DropdownMenuSeparator /> : null}
          </>
        ) : null}

        {canPin ? (
          <>
            <DropdownMenuItem
              onSelect={() => column.pin("left")}
              disabled={pinned === "left"}
              data-testid={`column-menu-pin-left-${column.id}`}
            >
              <Pin className="size-4" aria-hidden="true" />
              <span>Pin left</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => column.pin("right")}
              disabled={pinned === "right"}
              data-testid={`column-menu-pin-right-${column.id}`}
            >
              <Pin className="size-4 -scale-x-100" aria-hidden="true" />
              <span>Pin right</span>
            </DropdownMenuItem>
            {pinned ? (
              <DropdownMenuItem
                onSelect={() => column.pin(false)}
                data-testid={`column-menu-pin-clear-${column.id}`}
              >
                <PinOff className="size-4" aria-hidden="true" />
                <span>Unpin</span>
              </DropdownMenuItem>
            ) : null}
            {canHide ? <DropdownMenuSeparator /> : null}
          </>
        ) : null}

        {canHide ? (
          <DropdownMenuItem
            onSelect={() => column.toggleVisibility(false)}
            data-testid={`column-menu-hide-${column.id}`}
          >
            <EyeOff className="size-4" aria-hidden="true" />
            <span>Hide column</span>
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
